/**
 * Retail PO import from the GalaxyIQ template (any platform: Flipkart, FirstCry, Myntra, Amazon...),
 * for POs that do not come as an Amazon Vendor Central export. Moved here from the retired Excel Tools page.
 *
 *   "Template" downloads the .xlsx template; "Import from template" reads a filled file, shows a dry-run
 *   preview (new / updated POs, problem rows) and saves only on confirm. Rows with the same PO Number become
 *   one PO with several items; importing an existing PO Number replaces its items.
 */
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileSpreadsheet, RefreshCw, Upload } from 'lucide-react';
import { getAuthHeaders } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { ImportPreviewDialog } from './ImportPreviewDialog';
import { errorMessage } from './LoadError';
import { saveBase64File } from '../lib/download';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/retail-pos`;

const readAsBase64 = file => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const base64 = reader.result?.toString().split(',')[1];
    if (base64) resolve(base64); else reject(new Error('Failed to read file'));
  };
  reader.onerror = () => reject(new Error('Failed to read file'));
  reader.readAsDataURL(file);
});

export const RetailPOTemplateImport = ({ country, onImported }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [upload, setUpload] = useState(null); // { file: base64, name }
  const [preview, setPreview] = useState(null);

  const downloadTemplate = async () => {
    try {
      const { data } = await axios.get(`${API}/bulk-template`, { headers: getAuthHeaders() });
      saveBase64File(data.file, data.filename);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not download the template'));
    }
  };

  const pickFile = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const base64 = await readAsBase64(file);
      const { data } = await axios.post(`${API}/bulk-import?dry_run=true`, { file: base64, country, dry_run: true }, { headers: getAuthHeaders() });
      setUpload({ file: base64, name: file.name });
      setPreview(data);
    } catch (error) {
      toast.error('Could not read the PO file', { description: errorMessage(error, 'Use the PO template (.xlsx).') });
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    try {
      const { data } = await axios.post(`${API}/bulk-import`, { file: upload.file, country }, { headers: getAuthHeaders() });
      const skipped = data.errors?.length ? ` · ${data.errors.length} row(s) skipped` : '';
      toast.success(`${data.message || 'POs imported'}${skipped}`);
      onImported?.();
    } catch (error) {
      toast.error('PO import failed', { description: errorMessage(error) });
      throw error; // keep the preview open
    }
  };

  return (
    <>
      <Button variant="outline" onClick={downloadTemplate} className="h-9 px-3 text-[13px] rounded-lg" data-testid="po-template-download-btn"
        title="Template for POs that are not an Amazon Vendor export (Flipkart, FirstCry, Myntra...)">
        <FileSpreadsheet className="h-4 w-4 mr-2" aria-hidden="true" /> PO template
      </Button>
      <input ref={fileRef} type="file" accept=".xlsx" onChange={pickFile} className="hidden" data-testid="po-template-file-input" />
      <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} className="h-9 px-3 text-[13px] rounded-lg" data-testid="po-template-import-btn">
        {busy ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
        Import from template
      </Button>
      <ImportPreviewDialog
        open={!!preview}
        onOpenChange={value => { if (!value) { setPreview(null); setUpload(null); } }}
        title="Review Retail PO import"
        fileName={upload?.name}
        preview={preview}
        entityLabel="POs"
        confirmLabel="Import POs"
        onConfirm={confirm}
        testId="po-template-import-preview"
      />
    </>
  );
};
